import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import {
  TbArrowRight,
  TbBolt,
  TbChartDots3,
  TbCurrencyDollar,
  TbCalendarEvent,
  TbShield,
  TbSpeakerphone,
  TbSparkles,
  TbTicket,
} from "react-icons/tb";
import { organizerFeatures } from "./marketplaceHomeNewUIMockData";
import { PacManQR } from "./qrStudio/PacManQR";
import { encodeData } from "./qrStudio/qr-engine";
import { renderBrandedFrame, getQRInnerPaths, FRAME_OPTIONS } from "./qrStudio/qr-branded-frames";

const featureIcons = [TbCalendarEvent, TbTicket, TbChartDots3, TbSpeakerphone, TbCurrencyDollar, TbShield];

/* ─── QR Studio Section Data (parity with new-ui MarketplaceHome.tsx) ─── */
const QR_SHOWCASE_CODES = [
  { label: "Apple", dest: "https://apple.com", fg: "#000000", bg: "#FFFFFF", pattern: "square", corner: "sharp", category: "Technology", shape: "square", accent: "#A2AAAD", gradient: "from-slate-800 to-gray-700/60", logo: "⌘", logoBg: "#000000" },
  { label: "McDonald's", dest: "https://mcdonalds.com", fg: "#DA291C", bg: "#FFC72C", pattern: "square", corner: "sharp", category: "Food & Bev", shape: "square", accent: "#FFC72C", gradient: "from-yellow-950 to-amber-900/60", logo: "M", logoBg: "#DA291C" },
  { label: "Starbucks", dest: "https://starbucks.com", fg: "#00704A", bg: "#FFFFFF", pattern: "dots", corner: "rounded", category: "Coffee", shape: "circle", accent: "#00704A", gradient: "from-emerald-950 to-green-900/60", logo: "SB", logoBg: "#00704A" },
  { label: "TikTok", dest: "https://tiktok.com", fg: "#00F2EA", bg: "#010101", pattern: "dots", corner: "bullseye", category: "Social", shape: "square", accent: "#00F2EA", gradient: "from-cyan-950 to-teal-900/60", logo: "TT", logoBg: "#000000" },
  { label: "YouTube", dest: "https://youtube.com", fg: "#FF0000", bg: "#FFFFFF", pattern: "rounded", corner: "bullseye", category: "Video", shape: "square", accent: "#FF0000", gradient: "from-red-950 to-rose-900/60", logo: "▶", logoBg: "#FF0000" },
  { label: "Burger King", dest: "https://bk.com", fg: "#D62300", bg: "#F5EBDC", pattern: "square", corner: "sharp", category: "Food & Bev", shape: "square", accent: "#F5A623", gradient: "from-orange-950 to-amber-900/60", logo: "BK", logoBg: "#D62300" },
  { label: "Nike", dest: "https://nike.com", fg: "#111111", bg: "#FFFFFF", pattern: "diamond", corner: "sharp", category: "Sportswear", shape: "square", accent: "#111111", gradient: "from-zinc-900 to-neutral-800/60", logo: "✓", logoBg: "#111111" },
  { label: "Spotify", dest: "https://spotify.com", fg: "#1DB954", bg: "#191414", pattern: "dots", corner: "rounded", category: "Music", shape: "circle", accent: "#1DB954", gradient: "from-green-950 to-emerald-900/60", logo: "♫", logoBg: "#1DB954" },
  { label: "DHL", dest: "https://dhl.com", fg: "#D40511", bg: "#FFCC00", pattern: "square", corner: "sharp", category: "Logistics", shape: "square", accent: "#FFCC00", gradient: "from-yellow-950 to-red-900/60", logo: "DHL", logoBg: "#D40511" },
  { label: "Coca-Cola", dest: "https://coca-cola.com", fg: "#F40009", bg: "#FFFFFF", pattern: "rounded", corner: "rounded", category: "Beverage", shape: "circle", accent: "#F40009", gradient: "from-red-950 to-red-900/60", logo: "CC", logoBg: "#F40009" },
  { label: "Netflix", dest: "https://netflix.com", fg: "#E50914", bg: "#141414", pattern: "rounded", corner: "bullseye", category: "Streaming", shape: "square", accent: "#E50914", gradient: "from-red-950 to-zinc-900/60", logo: "N", logoBg: "#E50914" },
  { label: "Amazon", dest: "https://amazon.com", fg: "#FF9900", bg: "#232F3E", pattern: "instapass", corner: "rounded", category: "E-Commerce", shape: "square", accent: "#FF9900", gradient: "from-orange-950 to-amber-900/60", logo: "a→", logoBg: "#FF9900" },
  { label: "Amex", dest: "https://americanexpress.com", fg: "#006FCF", bg: "#FFFFFF", pattern: "square", corner: "sharp", category: "Finance", shape: "square", accent: "#006FCF", gradient: "from-blue-950 to-indigo-900/60", logo: "AX", logoBg: "#006FCF" },
  { label: "InstaPass", dest: "https://instapass.ai", fg: "#E52324", bg: "#FFFFFF", pattern: "instapass", corner: "bullseye", category: "Events", shape: "circle", accent: "#E52324", gradient: "from-red-950 to-red-900/60", logo: "IP", logoBg: "#E52324" },
];

const BRANDED_FRAMES_DATA = [
  { frameId: "ring-red", fg: "#E52324", pattern: "instapass", corner: "bullseye", cta: "SCAN TO UNLOCK EXCLUSIVE CONTENT" },
  { frameId: "ring-cyan", fg: "#00D9FF", pattern: "dots", corner: "rounded", cta: "SCAN FOR EVENT ACCESS" },
  { frameId: "badge-vip", fg: "#8B5CF6", pattern: "rounded", corner: "bullseye", cta: "VIP ENTRY" },
  { frameId: "badge-gold", fg: "#D4A017", pattern: "dots", corner: "rounded", cta: "SCAN TO SHOP" },
  { frameId: "badge-purple", fg: "#6D28D9", pattern: "rounded", corner: "bullseye", cta: "SCAN TO UNLOCK CONTENT" },
];

const SCROLLER_FRAME_CYCLE = [
  { frameId: "ring-red", cta: "SCAN NOW" },
  { frameId: "ring-cyan", cta: "SCAN TO ACCESS" },
  { frameId: "badge-vip", cta: "VIP ENTRY" },
  { frameId: "badge-gold", cta: "SCAN TO SHOP" },
  { frameId: "badge-purple", cta: "SCAN TO UNLOCK" },
];

function BrandedFramesRow() {
  const data = encodeData("https://instapass.ai");
  return (
    <div className="grid grid-cols-5 gap-2 sm:gap-3">
      {BRANDED_FRAMES_DATA.map((item) => {
        const frame = FRAME_OPTIONS.find((f) => f.id === item.frameId);
        const { paths, totalSize } = getQRInnerPaths(data.modules, data.size, item.fg, "#FFFFFF", item.pattern, item.corner, true, 2);
        const svg = renderBrandedFrame(paths, 480, totalSize, {
          frameId: item.frameId,
          ctaText: item.cta,
          watermarkText: "INSTAPASS",
          borderColor: frame.borderColor,
          accentColor: frame.accentColor,
        }, "#FFFFFF");
        return (
          <motion.div
            key={item.frameId}
            whileHover={{ scale: 1.06, y: -4 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="group cursor-pointer"
          >
            <div className="rounded-xl overflow-hidden bg-black/40 border border-white/[0.06] group-hover:border-white/[0.15] transition-all p-1.5 sm:p-2">
              <div
                dangerouslySetInnerHTML={{ __html: svg }}
                className="w-full aspect-square [&_svg]:w-full [&_svg]:h-full"
              />
            </div>
            <div className="text-center mt-1.5">
              <span className="text-[9px] sm:text-[10px] text-white/40" style={{ fontWeight: 600 }}>
                {frame.label}
              </span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function QRShowcaseScroller() {
  const scrollRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let frame;
    let pos = 0;
    const speed = 0.6;
    const animate = () => {
      if (!isHovered) {
        pos += speed;
        if (pos >= el.scrollWidth / 2) pos = 0;
        el.scrollLeft = pos;
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [isHovered]);

  const items = [...QR_SHOWCASE_CODES, ...QR_SHOWCASE_CODES];

  return (
    <div className="relative">
      <div
        className="relative rounded-2xl border border-[#E52324]/20 bg-[#0a0a0a]/90 backdrop-blur-xl overflow-hidden shadow-2xl"
        style={{ boxShadow: "0 8px 40px rgba(229,35,36,0.08), inset 0 1px 0 rgba(255,255,255,0.04)" }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#E52324]/[0.06] to-transparent pointer-events-none z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#E52324]/[0.06] to-transparent pointer-events-none z-10" />

        <div
          ref={scrollRef}
          className="flex items-center gap-4 sm:gap-5 overflow-hidden px-5 py-4"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {items.map((code, i) => {
            const frameCfg = SCROLLER_FRAME_CYCLE[i % SCROLLER_FRAME_CYCLE.length];
            const frameOpt = FRAME_OPTIONS.find((f) => f.id === frameCfg.frameId);
            const qrData = encodeData(code.dest);
            const { paths, totalSize } = getQRInnerPaths(qrData.modules, qrData.size, code.fg, code.bg, code.pattern, code.corner, true, 2);
            const svg = renderBrandedFrame(paths, 480, totalSize, {
              frameId: frameCfg.frameId,
              ctaText: `${frameCfg.cta} — ${code.label.toUpperCase()}`,
              watermarkText: code.label.toUpperCase(),
              borderColor: frameOpt.borderColor,
              accentColor: frameOpt.accentColor,
            }, code.bg);

            return (
              <motion.div
                key={`${code.label}-${i}`}
                className="shrink-0 group cursor-pointer"
                whileHover={{ scale: 1.08, y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ boxShadow: `0 0 20px ${code.accent}30, 0 0 40px ${code.accent}15`, transform: "scale(1.05)" }}
                  />
                  <div
                    className="w-[120px] h-[120px] sm:w-[140px] sm:h-[140px] rounded-xl overflow-hidden transition-all duration-300 ring-2 ring-transparent group-hover:ring-current"
                    style={{ color: code.accent }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: svg }}
                      className="w-full h-full [&_svg]:w-full [&_svg]:h-full"
                    />
                  </div>

                  <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div
                      className="px-2.5 py-1 rounded-lg backdrop-blur-md border whitespace-nowrap"
                      style={{ backgroundColor: `${code.accent}18`, borderColor: `${code.accent}30` }}
                    >
                      <div className="text-[9px] text-center" style={{ fontWeight: 700, color: code.accent }}>
                        {code.label}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function MarketplaceNewUISectionOrganizerQR() {
  return (
    <section className="bg-[#0a0a0a] py-20 lg:py-28 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E52324]/10 border border-[#E52324]/20 mb-6">
            <TbBolt className="w-3.5 h-3.5 text-[#E52324]" />
            <span className="text-[11px] tracking-[0.15em] uppercase text-[#E52324] font-black">Organizer Tools</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white mb-4">
            Everything You Need to <span className="text-[#E52324]">Sell Out</span>
          </h2>
          <p className="text-white/40 text-sm sm:text-base max-w-xl mx-auto">
            Powerful event creation tools and branded QR codes — all in one platform. No tech skills required.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl overflow-hidden bg-[#111] border border-white/10 shadow-lg"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0 rounded-3xl bg-gradient-to-br from-[#E52324]/[0.14] via-[#B01819]/[0.06] to-transparent"
            />
            <div className="relative z-10 p-8 sm:p-10">
              <div className="flex items-center gap-2 mb-5">
                <span className="w-6 h-px bg-[#E52324]" />
                <span className="text-[11px] tracking-[0.2em] uppercase text-[#E52324] font-black">For Event Organizers</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black uppercase leading-[0.92] tracking-tight text-white mb-4">
                Launch Your
                <br />
                Event on
                <br />
                <span className="text-[#E52324]">InstaPass</span>
              </h3>
              <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-md">
                Whether you&apos;re organizing a club night, a corporate conference, or a massive festival — InstaPass gives you the tools to sell out and scale up.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {organizerFeatures.map((feat, idx) => {
                  const Icon = featureIcons[idx] || TbSparkles;
                  return (
                    <motion.div
                      key={feat.title}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: 0.1 + idx * 0.05 }}
                      className="flex items-start gap-3 p-3.5 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/15 transition-all"
                    >
                      <div className={`w-9 h-9 rounded-lg ${feat.bg} flex items-center justify-center shrink-0`}>
                        <Icon className={`w-4 h-4 ${feat.color}`} />
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-xs uppercase tracking-tight mb-0.5">{feat.title}</h4>
                        <p className="text-white/40 text-[10px] leading-relaxed">{feat.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-3 mb-6">
                <Link
                  to="/create-event"
                  className="px-6 py-3 rounded-2xl bg-[#E52324] text-white text-sm font-black uppercase tracking-wider hover:bg-[#c91f20] transition-colors shadow-lg shadow-[#E52324]/20"
                >
                  Create Your First Event →
                </Link>
                <Link
                  to="/user/dashboard"
                  className="px-6 py-3 rounded-2xl border border-white/15 text-white/60 text-sm font-bold hover:bg-white/5 hover:text-white transition-all"
                >
                  Organizer Dashboard
                </Link>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-white/40">
                {["Free to create events", "No monthly fees", "Built-in fraud protection", "Fast payouts"].map((item) => (
                  <span key={item} className="flex items-center gap-1.5">
                    <FiCheck className="w-3 h-3 text-[#E52324]" />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative rounded-3xl overflow-hidden bg-[#0B0F19] border border-white/10 shadow-2xl"
          >
            <div className="relative z-10 p-8 sm:p-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E52324]/8 border border-[#E52324]/15 mb-6">
                  <TbSparkles className="w-3.5 h-3.5 text-[#E52324]" strokeWidth={2} />
                  <span className="text-[10px] text-[#E52324] uppercase tracking-[0.15em]" style={{ fontWeight: 800 }}>
                    SmartCodes by InstaPass
                  </span>
                </div>

                <h3
                  className="text-[28px] sm:text-[34px] leading-[1.05] text-white mb-4"
                  style={{ fontWeight: 900, fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
                >
                  Create QR Codes That
                  <br />
                  <span className="text-[#E52324]">Drive Action</span>
                </h3>

                <p className="text-[14px] text-white/35 leading-relaxed mb-8 max-w-md">
                  Design custom, branded QR codes in seconds. Track every scan with real-time analytics. Power your events, marketing, and business.
                </p>
              </motion.div>

              <div className="relative min-h-[320px] mb-10">
                <div className="flex items-center justify-center">
                  <PacManQR size={300} />
                </div>
              </div>

              <div className="mb-10">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h4
                      className="text-[16px] text-white mb-0.5"
                      style={{ fontWeight: 800, fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.02em" }}
                    >
                      Premium Branded Frames
                    </h4>
                    <p className="text-[11px] text-white/25">CTA text, watermarks & glow effects included</p>
                  </div>
                </div>
                <BrandedFramesRow />
              </div>

              <div className="pt-2 pb-2 -mx-8 sm:-mx-10">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 px-8 sm:px-10">
                  <div>
                    <h4
                      className="text-[16px] text-white mb-0.5"
                      style={{ fontWeight: 800, fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.02em" }}
                    >
                      Branded QR Codes for Every Use Case
                    </h4>
                    <p className="text-[11px] text-white/25">Fully customizable designs. Scroll to explore →</p>
                  </div>
                  <Link
                    to="/qr-studio/create"
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] text-[#E52324] border border-[#E52324]/20 hover:bg-[#E52324]/5 transition-all shrink-0"
                    style={{ fontWeight: 700 }}
                  >
                    Create Your Own <TbArrowRight className="w-3 h-3" strokeWidth={2} />
                  </Link>
                </div>
                <QRShowcaseScroller />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
